import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { A, pipe } from "@mobily/ts-belt"
import type { Planbook, PlanbookFlags } from "@shared/models/planbook"
import { handleError } from "cc-frontend/error-handling"
import type QueryService from "cc-frontend/services/query"
import type Session from "cc-frontend/services/session"
import { tracked } from "tracked-built-ins"
import styles from "./follow-course.module.css"

/** One course inside `importable_planbooks_for_user`'s minimal planbook representation. */
export interface ImportableCourse {
  id: string
  title: string
  color: string
  isNonInstructional: boolean
}

/** One planbook from `importable_planbooks_for_user`. */
export interface ImportablePlanbook {
  id: string
  title: string
  schoolYearId: string
  ownerFullName: string
  ownerId: string
  ownerFirstName: string
  ownerLastName: string
  ownerInitials: string
  cover: string
  flags: PlanbookFlags
  courses: Array<ImportableCourse>
}

interface PlanbooksByOwner {
  name: string
  initials: string
  planbooks: ReadonlyArray<ImportablePlanbook>
}

export interface FollowCourseModalSignature {
  Args: {
    model: {
      /** The following course's planbook; only its school year's courses are offered. */
      planbook: Planbook
      /** Left out of the list — a course can't follow itself. */
      courseId: string
      onSelectCourse: (planbook: ImportablePlanbook, course: ImportableCourse) => void
    }
    order: number
    closeModal: () => void
  }
}

export default class FollowCourseModalComponent extends Component<FollowCourseModalSignature> {
  readonly styles = styles

  @service declare query: QueryService
  @service declare session: Session

  @tracked isFetching = true
  @tracked importablePlanbooks: Array<ImportablePlanbook> = []

  constructor(owner: unknown, args: FollowCourseModalSignature["Args"]) {
    super(owner, args)
    this.fetchPlanbooks()
  }

  fetchPlanbooks() {
    this.query
      .fetch(
        this.query.generateStaticQuery("importable_planbooks_for_user", this.session.userId),
        0,
        200,
      )
      .then((response) => {
        if (this.isDestroyed || this.isDestroying) return
        this.importablePlanbooks = response.data.results.planbooks as Array<ImportablePlanbook>
        this.isFetching = false
      })
      .catch((error) => {
        if (this.isDestroyed || this.isDestroying) return
        this.isFetching = false
        if (error instanceof Error) handleError(error)
      })
  }

  /**
   * This school year's planbooks, minus the following course, "Just notes" classes (nothing to
   * follow), and any planbook left empty.
   */
  get sameYearPlanbooks(): Array<ImportablePlanbook> {
    const schoolYearId = this.args.model.planbook.attributes.schoolYearId

    return pipe(
      this.importablePlanbooks,
      A.filter((planbook) => planbook.schoolYearId === schoolYearId),
      A.map((planbook) => ({
        ...planbook,
        courses: planbook.courses.filter(
          (course) => course.id !== this.args.model.courseId && !course.isNonInstructional,
        ),
      })),
      A.reject((planbook) => planbook.courses.length === 0),
    )
  }

  get planbooksByOwner(): Array<PlanbooksByOwner> {
    const ownerId = this.session.userId

    return pipe(
      this.sameYearPlanbooks,
      A.sortBy((planbook) => planbook.title ?? ""),
      // "A1" sorts your own planbooks above everyone else's.
      A.sortBy((planbook) => (planbook.ownerId === ownerId ? "A1" : planbook.ownerLastName)),
      // Last, so starred planbooks come before all of that.
      A.sortBy((planbook) => (planbook.flags.isStarred === true ? 0 : 1)),
      (planbooks) => this.groupByOwner(planbooks),
    )
  }

  groupByOwner(planbooks: Array<ImportablePlanbook>): Array<PlanbooksByOwner> {
    const ownerId = this.session.userId

    return pipe(
      planbooks,
      A.groupBy((planbook) => planbook.ownerId),
      (grouped) => Object.entries(grouped),
      A.map(([id, ownerPlanbooks]) => ({
        name: id === ownerId ? "My Planbooks" : ownerPlanbooks![0].ownerFullName,
        initials: ownerPlanbooks![0].ownerInitials,
        planbooks: ownerPlanbooks!,
      })),
    )
  }

  @action
  selectCourse(planbook: ImportablePlanbook, course: ImportableCourse) {
    this.args.model.onSelectCourse(planbook, course)
    this.args.closeModal()
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Modals::FollowCourse": typeof FollowCourseModalComponent
  }
}
